import InfiniteScroll from 'react-infinite-scroll-component';
import isEmpty from 'lodash/isEmpty';
import { Dispatch, SetStateAction, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import HolderTransfersTabFilters from '@/modules/issuer-dashboard/components/HolderTransfersTabFilters.tsx';
import { IssuerTransferItem } from '@/modules/issuer-dashboard/components/items/IssuerTransferItem.tsx';
import { useIssuerTransferListQuery } from '@/modules/issuer-dashboard/queries/transfers.tsx';
import { EmptyListState, Spinner } from 'ui';
import { GetIssuerTransferListV1SrTransfersIssuerGetParams, ShareholderExtendResponse } from 'api';

export type transfersFilters = Pick<
  GetIssuerTransferListV1SrTransfersIssuerGetParams,
  'status' | 'sorted_by' | 'from_requested_at_date' | 'to_requested_at_date'
>;

interface TransfersTabProps {
  shareholder: ShareholderExtendResponse | null;
  isDeleted?: boolean;
  setIsDeleted?: Dispatch<SetStateAction<boolean | undefined>>;
}

const TransfersTab = ({ shareholder, isDeleted, setIsDeleted }: TransfersTabProps) => {
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<transfersFilters>({});

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useIssuerTransferListQuery(
    {
      page: 1,
      page_size: 15,
      general_shareholder_id: shareholder?.id,
      ...filters,
      is_deleted: isDeleted,
    },
    !shareholder,
    Number(params.get(REGISTER_ID_SEARCH_PARAM_KEY)),
  );

  const transfers = (data?.pages || []).flatMap(({ data }) => data);

  return (
    <div className="h-full space-y-4">
      <HolderTransfersTabFilters
        filters={filters}
        setFilters={setFilters}
        shareholder={shareholder}
        isDeleted={isDeleted}
        setIsDeleted={setIsDeleted}
        isFetching={isFetching}
      />
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      <EmptyListState
        title="No transfers yet"
        description="You can click on Add transfer to manually create transfer. Alternatively, investors can initiate transfers themselves from their dashboard but you will need to approve them."
        isLoading={isLoading}
        isSearchUsed={!isEmpty(filters)}
        itemsCount={transfers.length}
      />
      <InfiniteScroll
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        style={{ overflow: 'unset' }}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        className="space-y-2"
        dataLength={transfers.length}
        scrollableTarget="scrollable"
      >
        {transfers.map(transfer => (
          <IssuerTransferItem
            key={transfer.id}
            {...transfer}
            isDeleted={isDeleted}
            showArchiveButton={true}
          />
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default TransfersTab;
